import { useState, useEffect } from 'react'
import { useDroppedLoadReask } from '@papan/core-ui/ui/useDroppedLoadReask'
import { Loader2 } from 'lucide-react'
import { useFileIO } from '@papan/files/client/file-io'
import { useRealtime, useOnlineRecovery } from '@papan/realtime/client'
import { errorText } from '@papan/api-client'

interface ReadOnlyTextProps {
  fileId: string
  fileName?: string
  viewSync?: Record<string, Record<string, unknown>>
  viewSyncId?: string
  viewSyncTrigger?: number
}

export function ReadOnlyText({ fileId }: ReadOnlyTextProps) {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshCounter, setRefreshCounter] = useState(0)
  // #1316 review round 2: a load whose answer a session end dropped is asked again through `useDroppedLoadReask` —
  // in the share view it waits until the moved share session lands, never asking as the ended one
  const { reask, begin } = useDroppedLoadReask()
  const { onFileEvent } = useRealtime()
  const recovery = useOnlineRecovery()
  const fileIO = useFileIO()

  useEffect(() => {
    return onFileEvent((event) => {
      if (event.fileId === fileId && (event.type === 'file:updated' || event.type === 'file:restored')) {
        setRefreshCounter(c => c + 1)
      }
    })
  }, [fileId, onFileEvent])

  useEffect(() => {
    const asked = begin()   // the share session this load goes out as
    setLoading(true)
    setError(null)
    fileIO.loadContent(fileId)
      .then(blob => blob.text())
      .then(text => { setContent(text); setLoading(false) })
      .catch(err => {
        const text = errorText(err)
        // #1316: a dropped answer (its session ended) is asked again as the session now here — never an error
        if (text === null) { asked.dropped(); return }
        setError(text); setLoading(false)
      })
  }, [fileId, refreshCounter, recovery, fileIO, reask, begin])

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="animate-spin themed-text-muted" size={24} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center themed-text-muted">
        <p>Failed to load file: {error}</p>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <pre className="text-sm font-mono themed-text whitespace-pre-wrap break-words">{content}</pre>
    </div>
  )
}
